import { useEffect, useState } from "react";
import { fetchCatalog } from "../api/catalog";
import { loginCustomer, logoutCustomer } from "../api/customerAuth";
import { setCustomerSessionToken } from "../lib/customerSession";
import StoreBoot from "../components/StoreBoot";
import { storePath } from "../lib/storePaths";

const INVALID_CREDENTIALS_MESSAGE =
  "E-mail ou senha inválidos, ou não há uma conta para esta loja.";

export default function LoginPage({ storeSlug }) {
  const [store, setStore] = useState(null);
  const [storeError, setStoreError] = useState("");
  const [loadingStore, setLoadingStore] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const storeUrl = storePath("", storeSlug);
  const dashboardUrl = storePath("/dashboard", storeSlug);

  useEffect(() => {
    const controller = new AbortController();

    async function loadStore() {
      setLoadingStore(true);
      setStoreError("");

      try {
        const catalog = await fetchCatalog(storeSlug, {
          page: 1,
          limit: 1,
          signal: controller.signal,
        });

        if (!catalog?.store?.name) {
          throw new Error("A loja retornou uma resposta inválida.");
        }

        setStore(catalog.store);
        document.title = `Entrar | ${catalog.store.name}`;
      } catch (error) {
        if (error.name !== "AbortError") {
          setStoreError(
            error.status === 404
              ? "Esta loja não foi encontrada ou não está disponível."
              : "Não foi possível carregar esta loja. Tente novamente."
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingStore(false);
        }
      }
    }

    loadStore();
    return () => controller.abort();
  }, [storeSlug]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!store || submitting) return;

    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const email = String(form.get("email") || "").trim();
    const password = String(form.get("password") || "");
    setSubmitting(true);
    setFormError("");

    let issuedSessionToken = "";
    try {
      const result = await loginCustomer(storeSlug, { email, password });
      issuedSessionToken = result?.sessionToken;
      if (
        typeof issuedSessionToken !== "string" ||
        issuedSessionToken.length === 0
      ) {
        issuedSessionToken = "";
        throw new Error("The login response did not include a session token.");
      }

      setCustomerSessionToken(storeSlug, issuedSessionToken);
      window.location.assign(dashboardUrl);
    } catch {
      formElement.elements.password.value = "";
      if (issuedSessionToken) {
        await logoutCustomer(storeSlug, issuedSessionToken).catch(() => {});
      }
      setFormError(INVALID_CREDENTIALS_MESSAGE);
      setSubmitting(false);
    }
  }

  if (loadingStore && !storeError) return <StoreBoot />;

  const storeName = store?.name || "Sua loja";
  const initial = storeName.slice(0, 1).toLocaleUpperCase();

  return (
    <main className="login-page">
      <section className="login-card" aria-busy={loadingStore || submitting}>
        <a className="login-card__store" href={storeUrl}>
          <span className="store-mark" aria-hidden>
            {initial}
          </span>
          <span>
            <small>Loja oficial</small>
            <strong>{storeName}</strong>
          </span>
        </a>

        <div className="login-card__heading">
          <p className="login-card__eyebrow">Área do cliente</p>
          <h1>Entre na sua conta</h1>
          <p>Acesse sua conta vinculada a esta loja.</p>
        </div>

        {storeError ? (
          <div className="login-card__unavailable" role="alert">
            <p>{storeError}</p>
            <button type="button" onClick={() => window.location.reload()}>
              Tentar novamente
            </button>
          </div>
        ) : (
          <form className="login-form" onSubmit={handleSubmit}>
            <label htmlFor="customer-email">E-mail</label>
            <input
              id="customer-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="voce@exemplo.com"
              disabled={loadingStore || submitting}
              required
              autoFocus
            />

            <label htmlFor="customer-password">Senha</label>
            <input
              id="customer-password"
              name="password"
              type="password"
              autoComplete="current-password"
              placeholder="Digite sua senha"
              disabled={loadingStore || submitting}
              required
            />

            <div
              className="login-form__error"
              role="alert"
              aria-live="polite"
            >
              {formError}
            </div>

            <button
              className="login-form__submit"
              type="submit"
              disabled={loadingStore || submitting || !store}
            >
              {submitting ? (
                <>
                  <span className="spinner" aria-hidden />
                  Validando acesso...
                </>
              ) : loadingStore ? (
                "Carregando..."
              ) : (
                "Entrar"
              )}
            </button>
            <p className="login-form__session-note">
              Sua sessão permanece somente nesta aba do navegador.
            </p>
          </form>
        )}

        <a className="login-card__back" href={storeUrl}>
          Voltar para a loja
        </a>
      </section>
    </main>
  );
}
